import React, { useEffect, useRef, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { Box, Grid, InputAdornment, Typography } from '@mui/material';

import { formatCurrency, getRounding } from '../../utils/currencyHelper';
import useConfig from '../../context/ConfigContext';
import { CheckboxUI, InputUI } from '../Interface';
import { DocumentType } from '../../config/DocumentType';
import { canRoundOutgoingInvoice, isOutgoingInvoice, isValidInvoiceRounding } from '../../utils/invoiceRounding';

const toAbsoluteAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.abs(amount) : 0;
};

const getOpenDownPaymentAmount = (grossAmount, appliedGrossAmount) =>
  Math.max(toAbsoluteAmount(grossAmount) - toAbsoluteAmount(appliedGrossAmount), 0);

const DocumentSum = (props) => {
  const { values, setFieldValue } = useFormikContext();
  const { config } = useConfig();

  const { autocompleteData, documentData, docTotal, disabled, docTypeId } = props;

  const docCur = autocompleteData?.currency?.find((cur) => cur.code === values.currencyCode);
  const ownCur = autocompleteData?.currency?.find((cur) => cur.code === config.currencyCode);
  const isDownPaymentDocument = [DocumentType.DownPayment, DocumentType.APDownPayment].includes(Number(docTypeId));
  const outgoingInvoice = isOutgoingInvoice(docTypeId);
  const roundingAllowed = !outgoingInvoice || canRoundOutgoingInvoice(values, autocompleteData);

  const [isRoundingEnabled, setIsRoundingEnabled] = useState(false);
  const skipRoundingSyncRef = useRef(false);

  useEffect(() => {
    skipRoundingSyncRef.current = false;
    const isRoundingFilled =
      values.roundingInDocCur !== null &&
      values.roundingInDocCur !== undefined &&
      values.roundingInDocCur !== '' &&
      Number(values.roundingInDocCur) !== 0;
    setIsRoundingEnabled(isRoundingFilled);
  }, [values.docId]);

  useEffect(() => {
    if (
      disabled || !outgoingInvoice || roundingAllowed ||
      !autocompleteData?.currency?.length || !autocompleteData?.paymentMethod?.length
    ) return;

    setIsRoundingEnabled(false);
    if (values.roundingInDocCur !== null && values.roundingInDocCur !== undefined && values.roundingInDocCur !== '') {
      setFieldValue('roundingInDocCur', null);
      docTotal({ ...values, roundingInDocCur: null }, setFieldValue);
    }
  }, [disabled, outgoingInvoice, roundingAllowed, autocompleteData, values, setFieldValue, docTotal]);

  const handleRoundingToggle = (e) => {
    const checked = e.target.checked;
    skipRoundingSyncRef.current = true;
    setIsRoundingEnabled(checked);
    if (!checked) {
      setFieldValue('roundingInDocCur', null);
      docTotal({ ...values, roundingInDocCur: null }, setFieldValue);
    }
  };

  return (
    <Box>
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box mb={1.5}>
              <Typography variant="body2" color="text.secondary">
                Összesen bruttó:
              </Typography>
              <Typography variant="body1">
                {formatCurrency(values.sumInDocCur, docCur?.amountRounding ?? 0)} {docCur?.code}
              </Typography>
            </Box>
            <Box mb={1.5}>
              <Typography variant="body2" color="text.secondary">
                Összesen nettó:
              </Typography>
              <Typography variant="body1">
                {formatCurrency(values.netInDocCur, docCur?.amountRounding ?? 0)} {docCur?.code}
              </Typography>
            </Box>
            <Box mb={1.5}>
              <Typography variant="body2" color="text.secondary">
                Összesen Áfa:
              </Typography>
              <Typography variant="body1">
                {formatCurrency(values.VATInDocCur, docCur?.amountRounding ?? 0)} {docCur?.code}
              </Typography>
            </Box>
            {isDownPaymentDocument && (
              <>
                <Box mb={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Felhasznált előleg (bruttó):
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(values.appliedGrossAmount ?? 0, docCur?.amountRounding ?? 0)} {docCur?.code}
                  </Typography>
                </Box>
                <Box mb={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Nyitott előlegegyenleg (bruttó):
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(
                      getOpenDownPaymentAmount(values.sumInDocCur, values.appliedGrossAmount),
                      docCur?.amountRounding ?? 0
                    )}{' '}
                    {docCur?.code}
                  </Typography>
                </Box>
              </>
            )}
            <Box>
              {documentData.invoice && roundingAllowed && (
                <>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Kerekítés:
                    </Typography>
                    <CheckboxUI checked={isRoundingEnabled} onChange={handleRoundingToggle} disabled={disabled} />
                  </Box>
                  <Field
                    type="number"
                    name="roundingInDocCur"
                    as={InputUI}
                    variant="standard"
                    disabled={!isRoundingEnabled || disabled}
                    sx={{ width: '50%' }}
                    InputProps={{ endAdornment: <InputAdornment position="end">{docCur?.code}</InputAdornment> }}
                    inputProps={outgoingInvoice ? { min: -2, max: 2, step: 1 } : undefined}
                    value={values.roundingInDocCur ?? ''}
                    onChange={(e) => {
                      skipRoundingSyncRef.current = true;
                      const val = e.target.value === '' ? '' : Number(e.target.value);
                      if (outgoingInvoice && val !== '' && !isValidInvoiceRounding(val)) return;
                      setFieldValue('roundingInDocCur', val);
                      setIsRoundingEnabled(true);

                      docTotal({ ...values, roundingInDocCur: val }, setFieldValue);
                    }}
                  />
                </>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box mb={1.5}>
              <Typography variant="body2" color="text.secondary">
                Összesen bruttó saját pénznemben:
              </Typography>
              <Typography variant="body1">
                {formatCurrency(values.sumInOwnCur, ownCur?.amountRounding ?? 0)} {ownCur?.code}
              </Typography>
            </Box>
            <Box mb={1.5}>
              <Typography variant="body2" color="text.secondary">
                Összesen nettó saját pénznemben:
              </Typography>
              <Typography variant="body1">
                {formatCurrency(values.netInOwnCur, ownCur?.amountRounding ?? 0)} {ownCur?.code}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Összesen Áfa saját pénznemben:
              </Typography>
              <Typography variant="body1">
                {formatCurrency(values.VATInOwnCur, ownCur?.amountRounding ?? 0)} {ownCur?.code}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default DocumentSum;
